import {
    InsightType,
    type Status,
    type Step,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Tag } from "@amzn/storm-ui";
import { startCase } from "lodash";
import React from "react";
import { isNonNullish } from "remeda";

import { useContentCardShown } from "../../../../../hooks/misc/useContentCardShown";
import type { CardDetail } from "../../../../../types/card";
import { formatDateTime } from "../../../../../utils/date.utils";
import { getCardProgress } from "../../../../../utils/insightCard.utils";
import { getNormalizedAdvertiserId } from "../../../../../utils/permission.utils";
import { textToVariant } from "../../../../../utils/storm.utils";
import { TagList } from "../../../../common/styled.components";
import { CardLayout } from "../../insightDetails/CardLayout";
import { CardLayoutV3 } from "../../insightDetails/CardLayoutV3";
import type { GeneratedInsightCardProps } from "../GeneratedInsightCard";

import { CuratedInsightCardAction } from "./CuratedInsightCardAction";

export const CuratedInsightCard: React.FunctionComponent<
    GeneratedInsightCardProps
> = ({ insight }) => {
    const {
        identifier: executionId,
        title,
        step,
        status,
        metadata,
        updateTimestamp,
    } = insight;
    const advertiserId = getNormalizedAdvertiserId();
    const description = isNonNullish(metadata)
        ? metadata["description"]
        : undefined;

    const outputFileFormats = isNonNullish(metadata)
        ? metadata["outputFileFormats"] &&
          JSON.parse(metadata["outputFileFormats"])
        : ["pptx"];

    useContentCardShown({
        insightType: InsightType.WB,
        identifier: executionId,
    });

    const tags: string[] = React.useMemo(() => {
        const tags: string[] = [];
        if (
            metadata &&
            metadata["sourceSummaries"] &&
            Array.isArray(metadata["sourceSummaries"])
        ) {
            tags.push(...metadata["sourceSummaries"]);
        }
        return tags;
    }, [metadata]);

    const details: CardDetail[] = React.useMemo(() => {
        const details: CardDetail[] = [];
        if (metadata && metadata["description"]) {
            details.push({
                label: "Description",
                value: metadata["description"],
            });
        }
        if (tags.length > 0) {
            details.push({
                label: "Includes insights from",
                value: tags.map(startCase).join(", "),
            });
        }
        if (updateTimestamp) {
            details.push({
                label: "Compiled on",
                value: formatDateTime(updateTimestamp),
            });
        }
        return details;
    }, [metadata, tags, updateTimestamp]);

    if (metadata && metadata["globalWorkbench"]) {
        return (
            <CardLayoutV3
                key={executionId}
                cardHeader={title ?? ""}
                dataTestId={executionId ?? ""}
                insightType={InsightType.WB}
                insightTypeName="Workbench"
                description={description}
                renderBeforeFooter={
                    <TagList wrap="wrap">
                        {tags.map((tag) => (
                            <Tag
                                key={tag}
                                label={startCase(tag)}
                                variant={textToVariant(tag)}
                            />
                        ))}
                    </TagList>
                }
                footer={
                    updateTimestamp
                        ? `Compiled on: ${formatDateTime(updateTimestamp)}`
                        : "Compiled date unavailable"
                }
                actions={
                    <CuratedInsightCardAction
                        isGlobalWorkbench={true}
                        title={title!}
                        details={details}
                        executionId={executionId!}
                        advertiserId={advertiserId!}
                        progress={getCardProgress(
                            step as Step,
                            status as Status,
                        )}
                        description={description}
                        outputFileFormats={outputFileFormats}
                        insight={insight}
                    />
                }
            />
        );
    }
    return (
        <CardLayout
            key={executionId}
            dataTestId={executionId ?? ""}
            description={title ?? ""}
            details={details}
            actions={
                <CuratedInsightCardAction
                    title={title!}
                    details={details}
                    executionId={executionId!}
                    advertiserId={advertiserId!}
                    progress={getCardProgress(step as Step, status as Status)}
                    description={description}
                    outputFileFormats={outputFileFormats}
                    insight={insight}
                />
            }
        />
    );
};
